import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CatalogueVisiteMiseAJour,
  Devise,
  EtatCatalogue,
  PrixOeuvreCatalogue,
  StatutOeuvre,
} from '@artosera/shared';
import { api, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';
import { modeDemonstrationActif } from './demo.js';
import { DEMO_PDF_DOCUMENT_DATA_URI } from './demoFixtures.js';

export interface OeuvreCatalogueVisite {
  oeuvreId: string;
  jeton: string;
  titre: string;
  artisteNom: string;
  annee: number | null;
  statut: StatutOeuvre;
  mediaPrincipalId: string | null;
  masquee: boolean;
  prix: PrixOeuvreCatalogue;
  texteSalle: { fr: string; en?: string };
  prixAffiche: boolean;
  prixPublicCentimes: number | null;
  devise: Devise;
  servie: boolean;
  url: string;
}

export interface CatalogueVisitePresente extends Omit<CatalogueVisiteMiseAJour, 'oeuvres'> {
  id: string | null;
  existe: boolean;
  exposition: {
    id: string;
    titre: { fr: string; en?: string };
    debutLe: string;
    finLe: string | null;
  };
  jeton: string;
  texteEffectif: { fr: string; en: string };
  oeuvres: OeuvreCatalogueVisite[];
  nombreOeuvresServies: number;
  regenereLe: string | null;
  etat: EtatCatalogue;
  url: string;
}

export interface StatistiquesCatalogueVisite {
  depuis: string;
  jusqua: string;
  ouverturesParJour: { jour: string; accueil: number; oeuvres: number; total: number }[];
  oeuvres: { oeuvreId: string; ouvertures: number; demandes: number }[];
  topOeuvres: StatistiquesCatalogueVisite['oeuvres'];
}

export type FormatEtiquettesVisite = 'cartel' | 'a6' | 'a4-8';
const racine = (galerie: string, id: string) =>
  `/g/${encodeURIComponent(galerie)}/expositions/${encodeURIComponent(id)}/catalogue-visite`;

function pdfVisite(chemin: string, galerie: string, appel?: AppelApi) {
  if (modeDemonstrationActif()) {
    const contenu = atob(DEMO_PDF_DOCUMENT_DATA_URI.split(',')[1]!);
    return Promise.resolve({
      blob: new Blob([Uint8Array.from(contenu, (caractere) => caractere.charCodeAt(0))], {
        type: 'application/pdf',
      }),
      nomFichier: null,
    });
  }
  return requeteTelechargement(chemin, { galerie, ...appel });
}

export const catalogueVisiteApi = {
  obtenir: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<CatalogueVisitePresente>(racine(galerie, id), { galerie, ...appel }),
  enregistrer: (galerie: string, id: string, corps: CatalogueVisiteMiseAJour, appel?: AppelApi) =>
    api.put<CatalogueVisitePresente>(racine(galerie, id), corps, { galerie, ...appel }),
  regenerer: (galerie: string, id: string, appel?: AppelApi) =>
    api.post<CatalogueVisitePresente>(
      `${racine(galerie, id)}/regenerer`,
      {},
      { galerie, ...appel },
    ),
  statistiques: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<StatistiquesCatalogueVisite>(`${racine(galerie, id)}/statistiques`, {
      galerie,
      ...appel,
    }),
  lister: (galerie: string, appel?: AppelApi) =>
    api.get<{
      catalogues: {
        id: string;
        exposition: CatalogueVisitePresente['exposition'];
        etat: EtatCatalogue;
        nombreOeuvresServies: number;
        ouvertures30Jours: number;
      }[];
    }>(`/g/${encodeURIComponent(galerie)}/catalogues-visite`, { galerie, ...appel }),
  etiquettesPdf: (
    galerie: string,
    id: string,
    format: FormatEtiquettesVisite,
    langue: 'fr' | 'en',
    appel?: AppelApi,
  ) =>
    pdfVisite(
      `${racine(galerie, id)}/etiquettes.pdf?format=${format}&langue=${langue}`,
      galerie,
      appel,
    ),
  affichePdf: (galerie: string, id: string, langue: 'fr' | 'en', appel?: AppelApi) =>
    pdfVisite(`${racine(galerie, id)}/affiche.pdf?langue=${langue}`, galerie, appel),
};

const cleFiche = (galerie: string, id: string) => cleMetier(galerie, 'catalogue-visite', id);
export function useCatalogueVisite(galerie: string, id: string) {
  return useQuery({
    queryKey: cleFiche(galerie, id),
    queryFn: (contexte) => catalogueVisiteApi.obtenir(galerie, id, appelDeRequete(contexte)),
  });
}
export function useStatistiquesCatalogueVisite(galerie: string, id: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'catalogue-visite', id, 'statistiques'),
    queryFn: (contexte) => catalogueVisiteApi.statistiques(galerie, id, appelDeRequete(contexte)),
  });
}
export function useEnregistrerCatalogueVisite(galerie: string, id: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CatalogueVisiteMiseAJour, appel) =>
        catalogueVisiteApi.enregistrer(galerie, id, corps, appel),
      onSuccess: (catalogue) => client.setQueryData(cleFiche(galerie, id), catalogue),
    }),
  );
}
export function useRegenererCatalogueVisite(galerie: string, id: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (_: void, appel) => catalogueVisiteApi.regenerer(galerie, id, appel),
      onSuccess: (catalogue) => client.setQueryData(cleFiche(galerie, id), catalogue),
    }),
  );
}
